import _ from 'lodash';
import * as ApiRequests from '../../api/requests';
import { AuthHelper } from '../../types/auth-helper-types';
import { getPrecisionValue } from '../../lib/precision-helper';
import { DeviceListData } from '../../types/device';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getSiteCondition = async (authHelper: AuthHelper, args: { siteId: string }) => {
    const [siteStatusResult, siteDetailsResult] = await Promise.all([
        ApiRequests.getSiteStatus(
            {
                siteIdOrLocationIdOrOrganizationId: args.siteId,
            },
            { authHelper }
        ),
        ApiRequests.getSiteDetails(
            {
                siteId: args.siteId,
                priority: true,
            },
            { authHelper }
        ),
    ]);

    if (!siteStatusResult.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(siteStatusResult.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(siteStatusResult.response));
    }

    if (!siteDetailsResult.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(siteDetailsResult.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(siteDetailsResult.response));
    }

    const channelData = siteDetailsResult.ok ? siteDetailsResult.response.data.channelData : [];

    return {
        eventCount: undefined,
        online: siteStatusResult.ok ? siteStatusResult.response.data.status : undefined,
        offlinedevicecount: siteStatusResult.ok ? siteStatusResult.response.data.offlinedevicecount : undefined,
        channelData,
    };
};

type TrendData = Array<{
    id: number;
    channelName: string;
    deviceId: string;
    deviceName: string;
    value: Array<{ dateTime: string; value: string }>;
    unit: string | null;
}>;

const getTrendData = async (authHelper: AuthHelper, siteId: string, days?: number): Promise<TrendData> => {
    const trendsResult = await ApiRequests.getTrendsDetails({ siteId: siteId, days: days || 31 }, { authHelper });

    if (!trendsResult.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(trendsResult.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(trendsResult.response));
    }

    return trendsResult.response.data.trendData;
};

type TrendChannel = {
    maxTrendChannelToBeAllowedFromUser: number;
    trendChannels: Array<{ id: string; channelName: string; wasPriority: string | null }>;
};

const getTrendChannels = async (authHelper: AuthHelper, deviceId: string): Promise<TrendChannel> => {
    const trendsResult = await ApiRequests.getTrendChannels({ deviceId: deviceId }, { authHelper });

    if (!trendsResult.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(trendsResult.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(trendsResult.response));
    }

    return trendsResult.response.data;
};

const getDeviceTrendData = async (
    authHelper: AuthHelper,
    deviceId: string,
    deviceName: string,
    channels?: number[],
    days?: number
): Promise<TrendData> => {
    const trendsResult = await ApiRequests.getDeviceTrendsDetails(
        { deviceId: deviceId, days: days || 31, channels: channels || [] },
        { authHelper }
    );

    if (!trendsResult.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(trendsResult.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(trendsResult.response));
    }
    const result: TrendData = trendsResult.response.data.trendData.map((item: any) => ({
        ...item,
        deviceId,
        deviceName,
    }));

    return result;
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getDeviceCount = async (authHelper: AuthHelper, args: { siteId: string }) => {
    const result = await ApiRequests.getDevicesCount(
        {
            siteIdOrLocationIdOrOrganizationId: args.siteId,
        },
        { authHelper }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(result.response));
    }
    return result.ok ? result.response.data.deviceCount : 0;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getOnlineStatus = async (authHelper: AuthHelper, args: { siteId: string }) => {
    const [result] = await Promise.all([
        ApiRequests.getSiteStatus(
            {
                siteIdOrLocationIdOrOrganizationId: args.siteId,
            },
            { authHelper }
        ),
    ]);

    if (!result.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(result.response));
    }

    return {
        status: result.ok ? result.response.data.status : undefined,
        offlinedevicecount: result.ok ? result.response.data.offlinedevicecount : undefined,
    };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getSiteAlarmCount = async (authHelper: AuthHelper, args: { siteId: string }) => {
    const result = await ApiRequests.getAlarmCount(
        {
            siteIdOrLocationIdOrOrganizationId: args.siteId,
        },
        { authHelper }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(result.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }
    return result.ok ? result.response.data.alarmCount : undefined;
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getSiteBatteryStatus = async (authHelper: AuthHelper, args: { siteId: string }) => {
    const result = await ApiRequests.getSiteBatteryStatus(
        {
            siteIdOrLocationIdOrOrganizationId: args.siteId,
        },
        { authHelper }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(result.response));
    }

    return result.ok ? result.response.data.batteryStatus : null;
};
const getDevices = async (
    authHelper: AuthHelper,
    args: { siteId: string; recursive: boolean; status: boolean }
): Promise<DeviceListData[]> => {
    const result = await ApiRequests.getDevices(
        {
            siteIdOrLocationIdOrOrganizationId: args.siteId,
            recursive: args.recursive,
            status: args.status,
        },
        { authHelper }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(result.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }
    return result.ok
        ? (result.response.data.siteDevices || []).map((device) => ({
              id: device.deviceId,
              name: device.deviceName,
              type: device.deviceType,
              path: device.path,
              iconClass: device.iconClass,
              vendor: device.vendor,
              status: device.status,
              childDevicesExpected: device.childDevicesExpected,
              deviceCount: device.deviceCount,
              isEditable: device.isEditable,
              parentDeviceId: device.parentDeviceId,
              areAllChildrenOnline: device.areAllChildrenOnline,
              otaReleaseDetails: device.otaReleaseDetails,
              otaUpgradeStatus: device.otaUpgradeStatus,
              pxdbNavigation: false,
          }))
        : [];
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getChannelData = async (
    authHelper: AuthHelper,
    args: {
        siteId: string;
        limit?: number;
        priority?: boolean;
    }
) => {
    const result = await ApiRequests.getSiteDetails(
        {
            siteId: args.siteId,
            limit: args.limit,
            priority: args.priority,
        },
        { authHelper }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(result.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }

    return result.response.data.channelData
        .filter((channelDatum) => !_.isNil(channelDatum.channelName))
        .map((channelDatum) => ({
            channelName: channelDatum.channelName || '',
            deviceId: channelDatum.deviceId,
            deviceName: channelDatum.deviceName,
            value: getPrecisionValue(channelDatum.value),
            units: channelDatum.unit || '',
            id: channelDatum.id,
            iconClass: channelDatum.iconClass,
        }));
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getSites = async (authHelper: AuthHelper, args: { locationId: string }) => {
    const result = await ApiRequests.getSites(
        {
            locationId: args.locationId,
        },
        { authHelper }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(result.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }
    return result.response.data.sites || [];
};

//eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getSitesWithAlarmData = async (authHelper: AuthHelper, locationId: string) => {
    const siteListData = await getSites(authHelper, { locationId: locationId });

    const siteAlarmList = await Promise.all(
        siteListData &&
            siteListData.map(async (siteInfo) => {
                const alarmCountForSiteResult = await getSiteAlarmCount(authHelper, {
                    siteId: siteInfo.siteId,
                });
                return {
                    siteId: siteInfo.siteId,
                    siteName: siteInfo.siteName,
                    alarmCount: alarmCountForSiteResult ? alarmCountForSiteResult : 0,
                    deviceCount: siteInfo.deviceCount,
                };
            })
    );

    return siteAlarmList;
};

//eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getSiteProfile = async (authHelper: AuthHelper, siteId: string) => {
    const result = await ApiRequests.getSiteProfile(
        {
            siteId: siteId,
        },
        { authHelper }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(result.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }

    return {
        id: result.response.data.id,
        type: result.response.data.type,
        name: result.response.data.name,
        description: result.response.data.description,
        contact: result.response.data.contact,
        email: result.response.data.email,
        phone: result.response.data.phone,
        country:
            result.response.data.siteAttributes.country === null
                ? ''
                : result.response.data.siteAttributes.country || '',
        parentSiteId: result.response.data.parentSiteId,
        status: result.response.data.status,
        address: result.response.data.siteAttributes.address || '',
        city: result.response.data.siteAttributes.city === null ? '' : result.response.data.siteAttributes.city || '',
        state:
            result.response.data.siteAttributes.state === null ? '' : result.response.data.siteAttributes.state || '',
        zipCode:
            result.response.data.siteAttributes.zipCode === null
                ? ''
                : result.response.data.siteAttributes.zipCode || '',
        latitude:
            result.response.data.siteAttributes.latitude === undefined ||
            result.response.data.siteAttributes.latitude === null ||
            result.response.data.siteAttributes.latitude === 'NA'
                ? ''
                : result.response.data.siteAttributes.latitude,

        longitude:
            result.response.data.siteAttributes.longitude === undefined ||
            result.response.data.siteAttributes.longitude === null ||
            result.response.data.siteAttributes.longitude === 'NA'
                ? ''
                : result.response.data.siteAttributes.longitude,
    };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mappingSiteIdActivationId = async (
    authHelper: AuthHelper,
    args: { activationId: string; address: object; siteId: string }
) => {
    const result = await ApiRequests.mappingSiteIdActivationId(
        {
            activationId: args.activationId,
            address: args.address,
            siteId: args.siteId,
        },
        {
            authHelper: authHelper,
        }
    );

    if (!result.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(result.response));
        throw new Error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }
    return result.response.data || [];
};

export {
    getOnlineStatus,
    getSiteAlarmCount,
    getSiteBatteryStatus,
    getDevices,
    getChannelData,
    getSiteCondition,
    getTrendData,
    getDeviceTrendData,
    getDeviceCount,
    getSites,
    getSitesWithAlarmData,
    getSiteProfile,
    getTrendChannels,
    mappingSiteIdActivationId,
};
